import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, model, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { sortBy } from 'lodash';
import { forkJoin } from 'rxjs';
import { UiModule } from 'src/app/components/ui.module';
import { possibleBranchRoles, possibleLocationRoles, possibleSystemRoles } from '../../../constants';
import { UserAdminService } from '../../../service/user-admin.service';
import { TruncatePipe } from '../../../truncate.pipe';
import { Branch, UserLocationResponse, SystemRole, UserRoles } from '../../../types';

@Component({
  selector: 'app-user-role-selection',
  templateUrl: './user-role-selection.component.html',
  styleUrls: ['./user-role-selection.component.scss'],
  standalone: true,
  imports: [CommonModule, UiModule, FormsModule, MatTooltipModule, TruncatePipe],
})
export class UserRoleSelectionComponent implements OnInit {
  private adminService = inject(UserAdminService);

  public disabled = input<boolean>(false);
  public isLoading = signal<boolean>(false);
  public userRoles = model.required<UserRoles>();

  public allLocations = signal<UserLocationResponse[]>([]);
  public allBranches = signal<Branch[]>([]);

  public locationToAdd = signal<UserLocationResponse | undefined>(undefined);
  public branchToAdd = signal<Branch | undefined>(undefined);

  public branchesWithRoles = computed(() =>
    sortBy(
      Object.keys(this.userRoles().branch)
        .map((branchId) => this.allBranches().find((b) => b.id.toString() === branchId))
        .filter((b): b is Branch => !!b),
      'name',
    ),
  );
  public locationsWithRoles = computed(() =>
    sortBy(
      Object.keys(this.userRoles().location)
        .map((locationId) => this.allLocations().find((l) => l.id.toString() === locationId))
        .filter((l): l is UserLocationResponse => !!l),
      'name',
    ),
  );

  public hasBranchRoles = computed(() => this.branchesWithRoles().length > 0);
  public hasLocationRoles = computed(() => this.locationsWithRoles().length > 0);

  public readonly possibleSystemRoles = possibleSystemRoles;
  public readonly possibleLocationRoles = possibleLocationRoles;
  public readonly possibleBranchRoles = possibleBranchRoles;

  ngOnInit() {
    this.loadPageData();
  }

  private loadPageData() {
    this.isLoading.set(true);

    forkJoin({
      locations: this.adminService.getMyLocations(true),
      branches: this.adminService.getMyBranches(true),
    }).subscribe(({ locations, branches }) => {
      this.allLocations.set(sortBy(locations, (loc) => loc.name.toLowerCase().trim()));
      this.allBranches.set(sortBy(branches, (branch) => branch.name.toLowerCase().trim()));

      this.isLoading.set(false);
    });
  }

  public toggleSystemRole(roleId: SystemRole) {
    this.userRoles.update((current) => {
      const hasRole = current.system.includes(roleId);
      const updatedRoles = hasRole ? current.system.filter((r) => r !== roleId) : [...current.system, roleId];
      return { ...current, system: updatedRoles };
    });
  }

  public addLocation(location: UserLocationResponse) {
    this.userRoles.update((current) => {
      if (current.location[location.id]) {
        return current;
      }
      return { ...current, location: { ...current.location, [location.id]: [] } };
    });

    this.locationToAdd.set(undefined);
  }

  public removeLocation(location: UserLocationResponse) {
    this.userRoles.update((current) => {
      const updatedLocations = { ...current.location };
      delete updatedLocations[location.id];
      return { ...current, location: updatedLocations };
    });
  }

  public addBranch(branch: Branch) {
    this.userRoles.update((current) => {
      if (current.branch[branch.id]) {
        return current;
      }
      return { ...current, branch: { ...current.branch, [branch.id]: [] } };
    });

    this.branchToAdd.set(undefined);
  }

  public removeBranch(branch: Branch) {
    this.userRoles.update((current) => {
      const updatedBranches = { ...current.branch };
      delete updatedBranches[branch.id];
      return { ...current, branch: updatedBranches };
    });
  }
}
